import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import {
  StatusBadge,
  destinationLifecycleTone,
} from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { useOpenWizard } from "@/lib/wizard-navigation";
import type { Destination } from "@/lib/types";
import {
  mappingJsonForSave,
  parseMapping,
  StreamMappingFields,
  type MappingForm,
} from "@/features/streams/stream-mapping-fields";

type DestinationConfig = Destination & {
  mappingJson?: string | null;
  scheduleIntervalSeconds?: number | null;
  destinationCredentialId?: string | null;
  sourceConnectionId?: string | null;
};

export function ConferenceConfigurationPanel({
  orgId,
  conferenceId,
  canManage,
}: {
  orgId: string;
  conferenceId: string;
  canManage: boolean;
}) {
  const [destinations, setDestinations] = useState<DestinationConfig[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<MappingForm>(parseMapping(null));
  const [scheduleMinutes, setScheduleMinutes] = useState("60");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const openWizard = useOpenWizard();

  async function refresh() {
    const r = await api<{ streams: DestinationConfig[] }>(
      `/api/accounts/${orgId}/conferences/${conferenceId}/streams`,
    );
    setDestinations(r.streams);
    const current =
      r.streams.find((s) => s.id === selectedId) ?? r.streams[0] ?? null;
    if (current) {
      setSelectedId(current.id);
      setMapping(parseMapping(current.mappingJson));
      setScheduleMinutes(
        current.scheduleIntervalSeconds
          ? String(
              Math.max(1, Math.round(current.scheduleIntervalSeconds / 60)),
            )
          : "60",
      );
      setDirty(false);
    }
  }

  useEffect(() => {
    void refresh().catch(() => setDestinations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, conferenceId]);

  function selectDestination(id: string) {
    const dest = destinations.find((d) => d.id === id);
    if (!dest) return;
    setSelectedId(id);
    setMapping(parseMapping(dest.mappingJson));
    setScheduleMinutes(
      dest.scheduleIntervalSeconds
        ? String(Math.max(1, Math.round(dest.scheduleIntervalSeconds / 60)))
        : "60",
    );
    setDirty(false);
    setMessage(null);
    setError(null);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!selectedId || !canManage) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const minutes = Math.max(1, Number(scheduleMinutes) || 60);
      await api(
        `/api/accounts/${orgId}/conferences/${conferenceId}/streams/${selectedId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            mappingJson: mappingJsonForSave(mapping),
            scheduleIntervalSeconds: minutes * 60,
          }),
        },
      );
      setMessage("Destination configuration saved.");
      setDirty(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    if (!selectedId || !canManage) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/accounts/${orgId}/streams/${selectedId}/test`, {
        method: "POST",
        body: "{}",
      });
      setMessage("Test sync queued.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed");
    } finally {
      setBusy(false);
    }
  }

  async function setLifecycle(lifecycle: string) {
    if (!selectedId || !canManage) return;
    setBusy(true);
    setError(null);
    try {
      await api(
        `/api/accounts/${orgId}/conferences/${conferenceId}/streams/${selectedId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ lifecycle }),
        },
      );
      setMessage(`Destination marked ${lifecycle}.`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  const selected = destinations.find((d) => d.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <Panel>
        <PanelHeader
          title="Configuration"
          description="Per-destination mappings, schedules, and sync behavior"
          action={
            canManage ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openWizard()}
              >
                Add Destination
              </Button>
            ) : null
          }
        />
        {destinations.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-rs-ink-muted">
            No destinations configured for this conference yet.
          </p>
        ) : (
          <div className="grid gap-0 lg:grid-cols-[16rem_1fr]">
            <ul className="border-b border-rs-border lg:border-b-0 lg:border-r">
              {destinations.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm hover:bg-rs-muted ${
                      selectedId === d.id ? "bg-rs-accent-soft" : ""
                    }`}
                    onClick={() => selectDestination(d.id)}
                  >
                    <span className="capitalize font-medium text-rs-ink">
                      {d.destinationProvider}
                    </span>
                    <StatusBadge tone={destinationLifecycleTone(d.lifecycle)}>
                      {d.lifecycle}
                    </StatusBadge>
                  </button>
                </li>
              ))}
            </ul>

            {selected ? (
              <form className="space-y-5 p-5" onSubmit={save}>
                <div>
                  <h3 className="font-semibold capitalize text-rs-ink">
                    {selected.destinationProvider}
                  </h3>
                  <p className="text-sm text-rs-ink-muted">{selected.name}</p>
                </div>

                <StreamMappingFields
                  mapping={mapping}
                  scheduleMinutes={scheduleMinutes}
                  canManage={canManage}
                  idPrefix="conf-mapping"
                  showFieldMappingsNote
                  onMappingChange={(next) => {
                    setMapping(next);
                    setDirty(true);
                  }}
                  onScheduleMinutesChange={(next) => {
                    setScheduleMinutes(next);
                    setDirty(true);
                  }}
                />

                {canManage ? (
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" disabled={!dirty || busy}>
                      {busy ? "Saving…" : "Save Changes"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void runTest()}
                    >
                      Run Test Sync
                    </Button>
                    {selected.lifecycle === "draft" ||
                    selected.lifecycle === "tested" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          void setLifecycle(
                            selected.lifecycle === "draft"
                              ? "tested"
                              : "active",
                          )
                        }
                      >
                        {selected.lifecycle === "draft"
                          ? "Mark Tested"
                          : "Activate Destination"}
                      </Button>
                    ) : null}
                    {selected.lifecycle === "active" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => void setLifecycle("paused")}
                      >
                        Pause
                      </Button>
                    ) : null}
                    {selected.lifecycle === "paused" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => void setLifecycle("active")}
                      >
                        Resume
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-rs-ink-muted">
                    View only — ask an admin to change configuration.
                  </p>
                )}
                {message ? (
                  <p className="text-sm text-rs-success">{message}</p>
                ) : null}
                {error ? (
                  <p className="text-sm text-rs-danger">{error}</p>
                ) : null}
              </form>
            ) : null}
          </div>
        )}
      </Panel>
    </div>
  );
}
